import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

type RoleFilter = 'all' | 'company_admin' | 'module_user'

export interface CompanyUser {
  user_id: string
  role: string
  created_at: string
  id: string
  name: string
  email: string
  avatar_url?: string
}

export function useCompanyUsers(companyId: string, roleFilter: RoleFilter = 'all') {
  return useQuery({
    queryKey: ['company-users', companyId, roleFilter],
    queryFn: async () => {
      if (!companyId) return []

      console.log('🔍 Fetching users for company:', companyId, 'with role filter:', roleFilter)

      try {
        const { data, error } = await supabase.functions.invoke('get-company-users', {
          body: {
            company_id: companyId,
            role_filter: roleFilter
          }
        })

        if (error) {
          console.error('❌ Error fetching company users:', error)
          throw error
        }

        const combinedUsers: CompanyUser[] = data || []
        console.log('✅ Final combined users:', combinedUsers)
        
        return combinedUsers
      } catch (error) {
        console.error('❌ Failed to fetch company users:', error)
        throw error
      }
    },
    // Force fresh data to avoid stale cache
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    enabled: !!companyId,
  })
}

export function useCompanyUsersRealtime(companyId: string) {
  const queryClient = useQueryClient()

  // Note: This would require setting up realtime subscriptions
  // For now, we'll rely on manual refresh and cache invalidation
  const invalidateUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['company-users', companyId] })
  }

  return { invalidateUsers }
}